import { useLazyQuery, useQuery } from '@oneaudi/onegraph-client';
import { useAtom } from 'jotai';
import { useMarketConfig } from '~/app/market-config';
import { state } from '~/storage';
import {
	AcceptConflictSolutionDocument,
	AccessoriesDocument,
	AccessoriesQueryVariables,
	CarByCodeDocument,
	CarlineDocument,
	CatalogDocument,
	CatalogQueryVariables,
	ChangeConflictSolutionsDocument,
	ChangeIncentiveDocument,
	CheckConflictsAfterModelChangeDocument,
	CheckConflictsAfterTrimlineChangeDocument,
	CheckConflictsDocument,
	ConfiguredCarFeatureState,
	ConfiguredCarInput,
	ConfiguredCarTechnicalDataDocument,
	ConfiguredCarTrackingDocument,
	ConfiguredCarVehicleCodeDocument,
	FeaturesFilterInput,
	FinancingInput,
	ModelTechnicalDataDocument,
	PriceSummaryDocument,
	SimilarCarsDocument,
	SimilarCarsInput,
} from '../__generated__/graphql';

export function useCheckConflicts() {
	const [checkConflictAfterFeatureChange, queryFeatureChange] = useLazyQuery(CheckConflictsDocument, {
		errorPolicy: 'all',
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});

	const [checkConflictAfterModelChange, queryModelChange] = useLazyQuery(CheckConflictsAfterModelChangeDocument, {
		errorPolicy: 'all',
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});

	const [checkConflictAfterTrimlineChange, queryTrimline] = useLazyQuery(CheckConflictsAfterTrimlineChangeDocument, {
		errorPolicy: 'all',
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});

	const [checkConflictAfterIncentiveChange, queryIncentive] = useLazyQuery(ChangeIncentiveDocument, {
		errorPolicy: 'all',
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});

	return {
		checkConflictAfterFeatureChange,
		checkConflictAfterModelChange,
		checkConflictAfterTrimlineChange,
		checkConflictAfterIncentiveChange,
		isCheckingConflicts:
			queryFeatureChange.loading || queryModelChange.loading || queryTrimline.loading || queryIncentive.loading,
	};
}

const imageTypes = [
	'hd_image',
	'teaser_image',
	'wheel_image_small',
	'outer_tile_hd_square',
	'outer_tile_button',
	'wheel_image',
	'interior_tile_button',
	'interior_tile_hd_square',
	'hd_thumbnail',
	'small_hd_image',
	'outer_tile_hd',
	'steering_wheel_image_small',
	'seat_image_small',
	'seat_image',
	'folding_tile_button',
	'folding_tile_hd',
];

export const imageSetFilter = {
	types: imageTypes,
};

export const imageSetFilterStatic = {
	mediaSetTypes: imageTypes,
};

export type CheckConflictAfterFeatureChangeFn = ReturnType<typeof useCheckConflicts>['checkConflictAfterFeatureChange'];
export type CheckConflictsAfterModelChangeFn = ReturnType<typeof useCheckConflicts>['checkConflictAfterModelChange'];
export type CheckConflictsAfterIncentiveChangeFn = ReturnType<
	typeof useCheckConflicts
>['checkConflictAfterIncentiveChange'];

export function useChangeConflictSolutions() {
	const [changeConflictSolutions, query] = useLazyQuery(ChangeConflictSolutionsDocument, {
		errorPolicy: 'all',
		fetchPolicy: 'no-cache',
		returnPartialData: true,
	});

	return {
		changeConflictSolutions,
		isChangingConflictSolutions: query.loading,
	};
}

export function useAcceptConflictSolutions() {
	const [acceptConflictSolutions, query] = useLazyQuery(AcceptConflictSolutionDocument, {
		errorPolicy: 'all',
		fetchPolicy: 'no-cache',
		returnPartialData: true,
	});

	return {
		acceptConflictSolutions,
		isAcceptingConflictSolutions: query.loading,
	};
}

export function useCatalogQuery(configuredCar: ConfiguredCarInput, marketPackageFilter: FeaturesFilterInput) {
	const { data, previousData, loading } = useQuery(CatalogDocument, {
		variables: withFilters(configuredCar, marketPackageFilter, ['available'], false),
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});

	return {
		catalog: data ?? previousData,
		isLoadingCatalog: loading,
	};
}

export function usePriceSummaryQuery(configuredCar: ConfiguredCarInput) {
	const { data, previousData, loading } = useQuery(PriceSummaryDocument, {
		variables: {
			configuredCar,
		},
		fetchPolicy: 'no-cache',
		errorPolicy: 'ignore',
	});

	return {
		priceSummary: data ?? previousData,
		isLoadingPriceSummary: loading,
	};
}

export function useCarlineQuery(configuredCar: ConfiguredCarInput, financingInput: FinancingInput | null) {
	const { config } = useMarketConfig();
	const [financialError] = useAtom(state.financialError);
	const { data, previousData, loading } = useQuery(CarlineDocument, {
		errorPolicy: 'all',
		variables: {
			enablePartsRateCalculation:
				config.enablePartsRateCalculation && config.enableFinancingServices && !financialError,
			configuredCar,
			financingInput,
		},
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});

	return {
		carline: data ?? previousData,
		isLoadingCarline: loading,
	};
}

export function useAccessoriesQuery(configuredCar: ConfiguredCarInput) {
	const { data, previousData, loading } = useQuery(AccessoriesDocument, {
		variables: {
			configuredCar,
			accessoriesExclusive: {
				filterByFeatureState: ['available'],
				filterByFeatureGroups: {
					featureGroups: ['ACCESSORIES'],
					mode: 'EXCLUSIVE',
				},
				filterByFeatureTypes: {
					featureTypes: ['EQUIPMENT'],
				},
			},
			accessoriesInclusive: {
				filterByFeatureState: ['available'],
				filterByFeatureGroups: {
					featureGroups: ['ACCESSORIES'],
					mode: 'INCLUSIVE',
				},
			},
			imageTypeFilterInput: imageSetFilter,
		},
		errorPolicy: 'all',
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});

	return {
		accessories: data ?? previousData,
		isLoadingAccessories: loading,
	};
}

export function useConfiguredCarVehicleCodeLazyQuery() {
	const [fetchAudicode, { loading }] = useLazyQuery(ConfiguredCarVehicleCodeDocument, {
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});
	return { fetchAudicode, loading };
}

export function useCarByCode() {
	return useLazyQuery(CarByCodeDocument);
}

export function useConfiguredCarTechnicalDataLazyQuery() {
	const [fetchTechnicalData, { data, loading }] = useLazyQuery(ConfiguredCarTechnicalDataDocument, {
		errorPolicy: 'all',
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});
	return { fetchTechnicalData, data, loading };
}

export function useConfiguredCarTechnicalDataQuery(carIdentifier: ConfiguredCarInput) {
	return useQuery(ConfiguredCarTechnicalDataDocument, {
		errorPolicy: 'all',
		variables: {
			configuredCar: carIdentifier,
		},
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});
}

export function useModelTechnicalDataLazyQuery() {
	const [fetchModelData, { data, loading }] = useLazyQuery(ModelTechnicalDataDocument, {
		errorPolicy: 'all',
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});
	return { fetchModelData, data, loading };
}

export function useModelTechnicalDataQuery(carIdentifier: ConfiguredCarInput) {
	return useQuery(ModelTechnicalDataDocument, {
		errorPolicy: 'all',
		variables: {
			configuredCar: carIdentifier,
		},
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});
}

export function useConfiguredCarTrackingQuery(carIdentifier: ConfiguredCarInput) {
	return useQuery(ConfiguredCarTrackingDocument, {
		variables: {
			configuredCarIdentifier: carIdentifier,
		},
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});
}

export function useSimilarCarsQuery(
	carIdentifier: ConfiguredCarInput,
	financingInput: FinancingInput | null,
	{ maxNumberOfCars, geoSearch, sortCriterion, modelPrecision }: SimilarCarsInput,
) {
	// Image filter. Specify the ID of the image we want to use.
	// This returns only the specified ID(s). As we only use one picture per car, this removes a lot of uneccessary fetching.
	const VTP_IMAGE_ID_NEWCAR = 'sc900'; // VTP Id of presentation image for a new car (you have to look up the vtp id with an unfiltered query)
	const VTP_IMAGE_ID_USEDCAR = '0'; // Id of the presentation image of a used car (basically an array index)

	return useQuery(SimilarCarsDocument, {
		variables: {
			configuredCar: carIdentifier,
			similarCarsInput: {
				maxNumberOfCars,
				geoSearch,
				sortCriterion,
				modelPrecision,
			},
			newCarsImageIds: [VTP_IMAGE_ID_NEWCAR],
			usedCarsImageIds: [VTP_IMAGE_ID_USEDCAR],
		},
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});
}

export function withFilters(
	configuredCar: ConfiguredCarInput,
	marketPackageFilter: FeaturesFilterInput,
	filterByFeatureState: ConfiguredCarFeatureState[] = ['available'],
	financingEnabled: boolean,
): CatalogQueryVariables & AccessoriesQueryVariables {
	return {
		configuredCar,
		imageTypeFilterInput: imageSetFilter,
		accessoriesExclusive: {
			filterByFeatureState,
			filterByFeatureGroups: {
				featureGroups: ['ACCESSORIES'],
				mode: 'EXCLUSIVE',
			},
			filterByFeatureTypes: {
				featureTypes: ['EQUIPMENT'],
			},
		},
		accessoriesInclusive: {
			filterByFeatureState,
			filterByFeatureGroups: {
				featureGroups: ['ACCESSORIES'],
				mode: 'INCLUSIVE',
			},
		},
		steeringWheels: {
			filterByFeatureState,
			filterByFeatureGroups: {
				featureGroups: ['STEERING_WHEELS'],
			},
		},
		seats: {
			filterByFeatureGroups: {
				featureGroups: ['SEATS'],
			},
			filterByFeatureState,
		},
		interiorTiles: {
			filterByFeatureGroups: {
				featureGroups: ['INTERIOR_TILES'],
			},
		},
		exteriorColors: {
			filterByFeatureGroup: ['EXTERIOR_COLORS', 'EXCLUSIVE_EXTERIOR_COLORS'],
			filterByFeatureState,
		},
		bodyColors: {
			filterByFeatureGroup: ['BODY_COLOR'],
			filterByFeatureState,
		},
		roofColors: {
			filterByFeatureGroup: ['ROOF_COLOR'],
			filterByFeatureState,
		},
		exteriorDesign: {
			filterByFamilyIds: {
				familyIds: ['Lackierungen.Volllack', 'Lackierungen.Kontrastlack'],
			},
			filterByFeatureState,
		},
		headlights: {
			filterByFamilyIds: {
				familyIds: ['Scheinwerfer.Scheinwerfer'],
			},
			filterByFeatureState,
		},
		rims: {
			filterByFeatureState,
			filterByFeatureGroups: {
				featureGroups: ['RIMS'],
			},
		},
		packages: marketPackageFilter,
	};
}
